import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { showConfirm } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { bankService } from '@/services/bankService'
import { contractService } from '@/services/contractService'
import { BankListItem, EmployeeOption } from '@/api/types'
import { useEmployeeListScope } from '@/hooks/useEmployeeListScope'
import { EmployeeListScopeDialog } from '@/components/employee-scope/EmployeeListScopeDialog'

/**
 * Banks List Page
 * Displays a table of banks with CRUD operations
 */
export const BanksList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [banks, setBanks] = useState<BankListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [pendingAdd, setPendingAdd] = useState(false)

  const branchId = user?.selectedBranch?.branchId
  const { empId, pickerOpen, confirmEmployee, openEmployeePicker, handlePickerOpenChange } = useEmployeeListScope()

  // Fetch employees for mapping employee names
  useEffect(() => {
    if (!branchId) return

    const fetchEmployees = async () => {
      try {
        const employeesData = await contractService.getAllEmployees(branchId)
        setEmployees(employeesData || [])
        console.log('🔵 [BanksList] Loaded employees for mapping:', employeesData?.length || 0)
      } catch (error: any) {
        console.error('Error fetching employees for bank list:', error)
      }
    }

    fetchEmployees()
  }, [branchId])

  // Fetch banks
  useEffect(() => {
    if (!branchId || !empId) return

    const fetchBanks = async () => {
      try {
        setLoading(true)
        const data = await bankService.getBanksByEmpId(empId)
        setBanks(data || [])
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setBanks([])
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to fetch banks')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchBanks()
  }, [branchId, empId])

  useEffect(() => {
    if (empId) return
    setBanks([])
    setSearchQuery('')
    setCurrentPage(1)
    setPendingAdd(false)
  }, [empId])

  useEffect(() => {
    if (!pendingAdd || !empId) return
    setPendingAdd(false)
    navigate(`/banks/new?empId=${empId}`)
  }, [pendingAdd, empId, navigate])

  // Map employee names to banks
  const banksWithEmployeeNames = useMemo(() => {
    if (employees.length === 0) return banks
    
    return banks.map((bank) => {
      // If employee name already exists, use it
      if (bank.employeeName) return bank
      
      // Otherwise, find employee by empId and map name
      if (bank.empId) {
        const employee = employees.find((emp) => emp.empId === bank.empId)
        if (employee) {
          const fullName = employee.fullName || 
            [employee.empFirstNameEn, employee.empSecondNameEn, employee.empLastNameEn]
              .filter(Boolean)
              .join(' ')
              .trim() || 
            `Employee ${bank.empId}`
          return {
            ...bank,
            employeeName: fullName,
          }
        }
      }
      return bank
    })
  }, [banks, employees])

  // Client-side search and pagination filtering
  const filteredBanks = useMemo(() => {
    if (!searchQuery.trim()) {
      return banksWithEmployeeNames
    }

    const query = searchQuery.toLowerCase()
    return banksWithEmployeeNames.filter((bank) => {
      const employeeName = (bank.employeeName || '').toLowerCase()
      const bankName = (bank.bankName || '').toLowerCase()
      const accountTitle = (bank.accountTitle || '').toLowerCase()
      const accountNo = (bank.accountNo || '').toLowerCase()
      const iban = (bank.iban || '').toLowerCase()
      const note = (bank.note || '').toLowerCase()

      return (
        employeeName.includes(query) ||
        bankName.includes(query) ||
        accountTitle.includes(query) ||
        accountNo.includes(query) ||
        iban.includes(query) ||
        note.includes(query)
      )
    })
  }, [banksWithEmployeeNames, searchQuery])

  // Pagination
  const totalPages = Math.ceil(filteredBanks.length / pageSize)
  const paginatedBanks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredBanks.slice(startIndex, startIndex + pageSize)
  }, [filteredBanks, currentPage, pageSize])

  const handleAddBank = () => {
    navigate(`/banks/new?empId=${empId}`)
  }
  const handleAddBankClick = () => {
    if (empId) return handleAddBank()
    setPendingAdd(true)
    openEmployeePicker()
  }

  const handleEditBank = (id: number) => {
    navigate(`/banks/${id}/edit?empId=${empId}`)
  }

  const handleDeleteBank = async (id: number) => {
    const confirmed = await showConfirm(
      'Delete Bank',
      'Are you sure you want to delete this bank record?',
      'warning'
    )

    if (!confirmed) return

    try {
      await bankService.deleteBank(id)
      toast.success('Bank deleted successfully')
      // Reload banks
      if (branchId && empId) {
        const data = await bankService.getBanksByEmpId(empId)
        setBanks(data || [])
      }
    } catch (error: any) {
      console.error('Error deleting bank:', error)
      toast.error(error.response?.data?.message || 'Failed to delete bank')
    }
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
    } catch {
      return dateString
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('banks.title')}
          description={t('banks.description')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('banks.branchNotSelected')}
              description={t('banks.selectBranchToView')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <EmployeeListScopeDialog
        open={pickerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingAdd(false)
          handlePickerOpenChange(nextOpen)
        }}
        branchId={branchId}
        onConfirm={confirmEmployee}
        currentEmpId={empId}
      />
      <PageHeader
          title={t('banks.title')}
          description={
            empId
              ? `${t('banks.description')} · ${t('employeeListScope.selectedEmployee', { id: empId })}`
              : t('banks.description')
          }
          action={{
          label: t('common.add'),
          onClick: handleAddBankClick,
          icon: Plus,
          disabled: !empId,
        }}
      >
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openEmployeePicker}>
          <Users className="h-4 w-4" />
          {t('employeeListScope.changeEmployee')}
        </Button>
      </PageHeader>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t('banks.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                disabled={!empId}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('banks.pageSize')}</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange} disabled={!empId}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banks Table */}
      <Card>
        <CardContent className="p-0">
          {!empId ? (
            <div className="p-12">
              <EmptyState
                title={t('employeeListScope.dialogTitle', { defaultValue: 'Select employee' })}
                description={t('employeeListScope.dialogDescription', {
                  defaultValue:
                    'Choose an employee to load their records. You can change this later from the list page.',
                })}
                action={{
                  label: t('employeeListScope.changeEmployee'),
                  onClick: openEmployeePicker,
                }}
              />
            </div>
          ) : loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('banks.loading')}</p>
            </div>
          ) : filteredBanks.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('banks.noBanksFound')}
                description={
                  searchQuery
                    ? t('banks.tryAdjustingSearch')
                    : t('banks.getStartedAddFirst')
                }
                action={
                  !searchQuery
                    ? {
                        label: t('common.add'),
                        onClick: handleAddBankClick,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.employee')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.accountTitle')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.accountNo')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.iban')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.note')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.createdDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('banks.action')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBanks.map((bank) => (
                    <TableRow 
                      key={bank.id || bank.bankId} 
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {bank.employeeName || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {bank.accountTitle || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {bank.accountNo || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {bank.iban || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {bank.note || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {formatDate(bank.createdDate)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditBank(bank.id || bank.bankId || 0)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBank(bank.id || bank.bankId || 0)}
                            className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {t('banks.showing')} {(currentPage - 1) * pageSize + 1} {t('banks.to')}{' '}
                    {Math.min(currentPage * pageSize, filteredBanks.length)} {t('banks.of')} {filteredBanks.length} {t('banks.banks_count')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('banks.previous')}
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} {t('banks.of')} {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages}
                    >
                      {t('banks.next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
